/**
 * One dashboard widget — Step 50.
 *
 * Every kind draws the SAME answer differently: `POST /api/dashboards/data`
 * takes a filter and optionally groups it, so a scorecard shows the total and
 * a chart shows the groups. One query, one component, rather than three of
 * each drifting apart.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { cn } from '../../lib/utils';

export interface Widget {
  id: string;
  kind: 'scorecard' | 'donut' | 'bar' | 'activity';
  title: string;
  viewId?: string | null;
  groupBy?: string | null;
  warnAt?: number | null;
  criticalAt?: number | null;
  /** The card's resting tint. Thresholds override it — a breach has to be the
   *  loudest thing on the board, so a green card that is over its critical
   *  number would be actively misleading. */
  colour?: string | null;
  span?: number;
}

/** A small, named palette. Not a colour picker: a wall of arbitrary hues is
 *  unreadable, and these are the tokens the rest of the app already uses. */
export const WIDGET_COLOURS = [
  { value: '', label: 'None' },
  { value: 'brand', label: 'Blue' },
  { value: 'emerald', label: 'Green' },
  { value: 'amber', label: 'Amber' },
  { value: 'purple', label: 'Purple' },
  { value: 'sky', label: 'Sky' },
  { value: 'rose', label: 'Rose' },
] as const;

const COLOUR_CLASS: Record<string, string> = {
  brand: 'border-brand/40 bg-brand/5',
  emerald: 'border-emerald-500/40 bg-emerald-500/5',
  amber: 'border-amber-500/40 bg-amber-500/5',
  purple: 'border-purple-500/40 bg-purple-500/5',
  sky: 'border-sky-500/40 bg-sky-500/5',
  rose: 'border-rose-500/40 bg-rose-500/5',
};
interface Data {
  total: number;
  groups: { key: string; label: string; count: number }[];
}

/** Enough distinct hues to read, and no more: a chart with fourteen colours
 *  is a table wearing a costume. */
const SLICE = [
  'bg-brand',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-sky-500',
  'bg-rose-500',
  'bg-muted-foreground',
];

/** groupBy → the queue's chip param. The queue already filters on all five;
 *  a drill-through just adds one to the view's own URL. */
const DRILL_PARAM: Record<string, string> = {
  status: 'status',
  priority: 'priority',
  type: 'type',
  group: 'group',
  assignee: 'assignee',
};

export function WidgetCard({ w, onEdit }: { w: Widget; onEdit?: () => void }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: qk.dashboards.data(w.viewId ?? '', w.groupBy ?? ''),
    queryFn: async () => {
      const r = await apiSend<Data>('/api/dashboards/data', 'POST', {
        viewId: w.viewId ?? null,
        groupBy: w.groupBy ?? null,
      });
      if (!r.ok || !r.body) throw new Error(r.error ?? 'failed');
      return r.body;
    },
    staleTime: 60_000,
  });

  /**
   * A breached threshold tints the WHOLE card, not a corner badge. The point
   * of a scorecard is being readable across a room; if the warning has to be
   * found, it is not doing the job the card exists for.
   */
  const total = q.data?.total ?? 0;
  const breach =
    w.criticalAt != null && total >= w.criticalAt
      ? 'critical'
      : w.warnAt != null && total >= w.warnAt
        ? 'warn'
        : null;

  /** The view, plus the slice that was clicked. Kai: *"things where views are
   *  sorted by X you should be able to click on any one to get the filter with
   *  the extra thing and see just those"*. */
  const open = (slice?: { key: string }) => {
    if (!w.viewId) return;
    const p = new URLSearchParams({ view: w.viewId });
    const param = w.groupBy ? DRILL_PARAM[w.groupBy] : null;
    if (slice && param) p.set(param, slice.key || 'none');
    navigate(`/tickets?${p}`);
  };

  return (
    <div
      // The WHOLE tile is the target for a scorecard (Kai) — a number you have
      // to aim at is a number people stop clicking. In edit mode the click
      // opens the widget's settings instead, because that is what you came for.
      onClick={onEdit ? undefined : w.kind === 'scorecard' ? () => open() : undefined}
      className={cn(
        'rounded-lg border bg-card p-4',
        // Resting colour first, so a breach can override it below.
        !breach && w.colour && COLOUR_CLASS[w.colour],
        !onEdit && w.kind === 'scorecard' && w.viewId && 'cursor-pointer hover:border-brand/50',
        breach === 'warn' && 'border-amber-500/50 bg-amber-500/10',
        breach === 'critical' && 'border-destructive/50 bg-destructive/10',
      )}
    >
      <div className="mb-2 flex items-center gap-1.5">
        {/* The TITLE opens the whole thing; a row opens its slice. Kai:
            *"I could click engineering to open that sub set of the original
            filter, or the overall thing to get the overall"*. On a scorecard
            the tile already is the target, so the heading stays plain there
            rather than being a button inside a button. */}
        {w.kind !== 'scorecard' && w.viewId && !onEdit ? (
          <button
            type="button"
            onClick={() => open()}
            title={`Open ${w.title}`}
            className="min-w-0 flex-1 truncate rounded text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {w.title}
          </button>
        ) : (
          <h3 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {w.title}
          </h3>
        )}
        {breach && (
          <AlertTriangle
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              breach === 'critical' ? 'text-destructive' : 'text-amber-600 dark:text-amber-400',
            )}
          />
        )}
      </div>

      {q.isPending && <Skeleton className="h-12 w-full" />}
      {q.isError && <p className="text-sm text-destructive">Could not load</p>}

      {q.data && w.kind === 'scorecard' && <p className="text-3xl font-semibold tabular-nums">{total}</p>}

      {q.data && (w.kind === 'donut' || w.kind === 'bar') && (
        <div className="space-y-1.5">
          {q.data.groups.length === 0 && <p className="text-sm text-muted-foreground">Nothing to show.</p>}
          {q.data.groups.slice(0, 7).map((g, i) => (
            <button
              key={g.key}
              type="button"
              disabled={!w.viewId || !!onEdit}
              onClick={() => open(g)}
              title={`Open ${g.label}`}
              className="flex w-full items-center gap-2 rounded text-left hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:hover:bg-transparent"
            >
              <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">{g.label}</span>
              {/* A horizontal bar with the label beside it, not a pie: it
                  reads at any size and needs no legend, which is the half of
                  a pie chart nobody reads. */}
              <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                <span
                  className={cn('block h-full rounded-full', SLICE[i % SLICE.length])}
                  style={{ width: `${total ? Math.max(2, (g.count / total) * 100) : 0}%` }}
                />
              </span>
              <span className="w-8 shrink-0 text-right text-xs tabular-nums">{g.count}</span>
            </button>
          ))}
          {q.data.groups.length > 7 && (
            <p className="text-xs text-muted-foreground">+{q.data.groups.length - 7} more</p>
          )}
        </div>
      )}
    </div>
  );
}
